import React, { useState, useEffect } from "react";
import { Form, Modal, Button, Space, Typography, Tag, Alert } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Error, Success } from "../../../../commons/Messages/Messages";
import { generalErrorAlert } from "../../../../commons/GeneralErrorAlert";
import MainButtonComp from "../../../../commons/Buttons/MainButtonComp";
import GeneralModal from "../../General.modal";
import { isRecurringShift } from "../../../../utils/shiftUtils";
import { saveWithConflictCheck, shiftApi } from "../shiftApi";
import {
  ShiftFormFields,
  EmployeeSelect,
  mergeEmployees,
  shiftToFormValues,
  formValuesToShift,
  validateShiftValues
} from "./ShiftFormFields";

const { Text } = Typography;

/**
 * Edit a stored shift (`data`). When opened from a calendar occurrence of a recurring shift,
 * a second tab edits only that day (staff, cancel/restore) without touching the series.
 */
const UpdateShift = ({
  updateParent,
  data,
  occurrence,
  showEventModal,
  onClose,
  employees,
  onDuplicate
}) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [dayForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("series");

  const staffOptions = mergeEmployees(employees || [], [
    ...(data?.employees || []),
    ...(occurrence?.employees || [])
  ]);
  const dayEditable = !!occurrence && isRecurringShift(data);
  const dayLabel = occurrence ? moment(occurrence.start).format("ddd DD/MMM/YYYY") : "";
  const dayException = (data?.exceptions || []).find(exc => exc.date === occurrence?._dateKey);

  const showModal = () => setIsModalOpen(true);
  const handleCancel = () => {
    setIsModalOpen(false);
    setActiveTab("series");
    onClose && onClose();
  };

  useEffect(() => {
    if (!data || !isModalOpen) return;
    form.setFieldsValue(shiftToFormValues(data));
    if (occurrence) {
      dayForm.setFieldsValue({ employees: (occurrence.employees || []).map(e => e?._id || e) });
    }
  }, [data, occurrence, isModalOpen]);

  useEffect(() => {
    if (showEventModal) showModal();
  }, [showEventModal]);

  const finish = res => {
    if (!res) return;
    Success(res.data.successMessage);
    updateParent && updateParent();
    handleCancel();
  };

  const saveSeries = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const problem = validateShiftValues(values, t);
    if (problem) {
      Error(problem);
      return;
    }
    finish(await saveWithConflictCheck("put", `/update/${data._id}`, formValuesToShift(values), t));
  };

  const saveDay = async body =>
    finish(
      await saveWithConflictCheck(
        "put",
        `/occurrence/${data._id}`,
        { date: occurrence._dateKey, ...body },
        t
      )
    );

  const onSubmit = async () => {
    if (loading) return;
    setLoading(true);
    try {
      if (activeTab === "day") {
        await saveDay({ employees: dayForm.getFieldValue("employees") || [] });
      } else {
        await saveSeries();
      }
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setLoading(false);
    }
  };

  const runDayAction = async body => {
    try {
      await saveDay(body);
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const onDelete = () => {
    Modal.confirm({
      title: t("Delete Shift"),
      content: `${t("Delete")} "${data?.title}"? ${t("This removes every day of this shift.")}`,
      okText: t("Yes"),
      okButtonProps: { danger: true },
      cancelText: t("No"),
      onOk: async () => {
        try {
          finish(await shiftApi.send("delete", `/delete/${data?._id}`));
        } catch (err) {
          generalErrorAlert(err);
        }
      }
    });
  };

  const seriesForm = (
    <Form form={form} layout="vertical" initialValues={shiftToFormValues(data)}>
      {onDuplicate && (
        <Button
          icon={<CopyOutlined />}
          style={{ marginBottom: 16 }}
          onClick={() => {
            const values = form.getFieldsValue();
            handleCancel();
            onDuplicate({ ...values, title: `${values.title || ""} (${t("copy")})` });
          }}
        >
          {t("Duplicate as new shift")}
        </Button>
      )}
      <ShiftFormFields employees={staffOptions} t={t} />
    </Form>
  );

  const dayPanel = dayEditable && (
    <Form form={dayForm} layout="vertical">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={`${t("Changes here only affect")} ${dayLabel}. ${t("The rest of the shift stays as it is.")}`}
      />
      <Space wrap style={{ marginBottom: 16 }}>
        <Text strong>
          {moment(occurrence.start).format("HH:mm")} – {moment(occurrence.end).format("HH:mm")}
        </Text>
        {occurrence.cancelled && <Tag color="red">{t("Cancelled")}</Tag>}
        {occurrence._moved && <Tag color="blue">{t("Moved")}</Tag>}
        {occurrence._customStaff && <Tag color="purple">{t("Own staffing")}</Tag>}
      </Space>
      <Form.Item name="employees" label={t("Employees on this day")}>
        <EmployeeSelect employees={staffOptions} t={t} disabled={occurrence.cancelled} />
      </Form.Item>
      <Space wrap>
        <Button
          danger={!occurrence.cancelled}
          onClick={() => runDayAction({ cancelled: !occurrence.cancelled })}
        >
          {occurrence.cancelled ? t("Restore this day") : t("Cancel this day")}
        </Button>
        {dayException && (
          <Button onClick={() => runDayAction({ reset: true })}>
            {t("Undo all changes for this day")}
          </Button>
        )}
      </Space>
    </Form>
  );

  const items = dayEditable
    ? [
        { key: "series", label: t("Whole shift"), children: seriesForm, forceRender: true },
        { key: "day", label: `${t("Only")} ${dayLabel}`, children: dayPanel, forceRender: true }
      ]
    : seriesForm;

  return (
    <div className="UpdateShift">
      {!showEventModal && data?._id && <MainButtonComp text="Edit" editBtn onClick={showModal} />}
      <GeneralModal
        title={t("Update Shift")}
        open={isModalOpen}
        onOk={onSubmit}
        okText={t("Save")}
        onCancel={handleCancel}
        onChange={setActiveTab}
        items={items}
        wEventclicked="updateordelete"
        deleteEvent={onDelete}
      />
    </div>
  );
};

export default UpdateShift;
